/**
 * "What this exercise adds" — its points, broken out by what they move.
 *
 * Directions by default, because that is what the surrounding screens are
 * organised by. `patterns` adds the finer row underneath, for the screens with
 * room to say which specific patterns a task is actually working on — an
 * exercise can name three, and only showing its track hid two of them.
 *
 * Chips stay neutral for directions the user has not taken, because a coloured
 * chip would imply a score.
 */
import React from 'react';
import { useT } from '../i18n/useT';
import { Icon } from './icons';
import { Meta, Row } from './primitives';
import { AppContent, Exercise } from './content';
import { c, scoreColor, patternZone } from './tokens';
import { splitPoints } from './points';

export { splitPoints } from './points';

const Chip: React.FC<{
    col: string;
    pts: number;
    label: string;
    icon?: string;
    dot?: boolean;
}> = ({ col, pts, label, icon, dot }) => (
    <Row
        style={{
            gap: 5,
            padding: icon ? '3px 8px 3px 6px' : '3px 9px',
            borderRadius: 99,
            border: `1px solid ${col}33`,
            background: `${col}0f`,
            minWidth: 0,
        }}
    >
        {icon && <Icon k={icon} size={11} color={col} />}
        {dot && <span style={{ width: 5, height: 5, borderRadius: '50%', background: col, flex: '0 0 auto' }} />}
        <Meta style={{ fontSize: 9.5, color: col, letterSpacing: '0.08em' }}>+{pts}</Meta>
        <Meta
            style={{
                fontSize: 9.5,
                color: c.muted,
                letterSpacing: '0.08em',
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {label}
        </Meta>
    </Row>
);

export const XpTag: React.FC<{
    ex: Exercise;
    content: AppContent;
    scoreOf: (code: string) => number | null;
    /** Also list the individual patterns the points are split across. */
    patterns?: boolean;
    style?: React.CSSProperties;
}> = ({ ex, content, scoreOf, patterns, style }) => {
    const t = useT();
    const split = splitPoints(ex, content);

    const dirs = (
        <Row style={{ gap: 8, flexWrap: 'wrap' }}>
            {split.byDirection.map(([code, pts]) => {
                const score = scoreOf(code);

                return (
                    <Chip
                        key={code}
                        col={score == null ? 'rgba(232,230,224,0.45)' : scoreColor(score).hex}
                        pts={pts}
                        icon={code}
                        label={content.dirBy[code]?.disp || code}
                    />
                );
            })}
        </Row>
    );

    if (!patterns || !split.byPattern.length) return <div style={style}>{dirs}</div>;

    return (
        <div style={style}>
            {dirs}
            <Row style={{ gap: 8, flexWrap: 'wrap', marginTop: 7 }}>
                {split.byPattern.map(([code, pts]) => {
                    const p = content.patBy[code];
                    const score = scoreOf(p?.dim || ex.track);

                    return (
                        <Chip
                            key={code}
                            col={score == null ? 'rgba(232,230,224,0.35)' : patternZone(score, p?.pol).hex}
                            pts={pts}
                            dot
                            label={p?.name || code}
                        />
                    );
                })}
            </Row>
            {split.byPattern.length > 1 && (
                <Meta style={{ fontSize: 9, color: c.subtle, display: 'block', marginTop: 7 }}>
                    {t('app.task.splitNote', { n: split.byPattern.length, total: split.total })}
                </Meta>
            )}
        </div>
    );
};
